import React, {ReactElement} from 'react';
import clsx from 'clsx';
import {KeyboardSymbolKind} from 'ui/keyboard';
import {PressEventProvider, PressEventProps} from 'components/PressEventProvider';
import {LongPressProvider, LongPressEventProps} from 'components/LongPressProvider';
import {Wrapper, Inner} from './styles';
import {isSpend, useInputMode} from 'store/UserInput';
import {useUIStore} from 'store/UI';

interface KeyProps {
  symbol: KeyboardSymbol;
  disabled?: boolean;
  className?: ClassName;
  onPress?: () => void;
  onLongPress?: () => void;
  delayMS?: number;
}

function selectColor(
  {type}: KeyboardSymbol,
  mode: UserInputMode,
  settingsVisible: boolean
): ClassName {
  const {Backspace, Enter, ModeToggle} = KeyboardSymbolKind;
  const isSpendMode = isSpend(mode);

  switch (type) {
    case Backspace:
    case ModeToggle: {
      if (settingsVisible) return 'beige';
      if (isSpendMode) return 'beige';
      return 'sage';
    }

    case Enter: {
      if (isSpendMode || settingsVisible) return 'yellow';
      return 'green';
    }

    default:
      return '';
  }
}

function selectIcon({type}: KeyboardSymbol, mode: UserInputMode): ClassName {
  const {Backspace, Comma, Enter, ModeToggle} = KeyboardSymbolKind;

  switch (type) {
    case Backspace:
      return 'backspace';

    case Comma:
      return 'comma';

    case Enter:
      return 'enter';

    case ModeToggle:
      return isSpend(mode) ? 'subtract' : 'add';

    default:
      return '';
  }
}

export const Key = ({
  symbol,
  className,
  disabled,
  onPress,
  onLongPress,
  delayMS,
}: KeyProps): ReactElement => {
  const inputMode = useInputMode();
  const {settingsVisible} = useUIStore();
  const classNames = clsx(
    className,
    {disabled},
    selectColor(symbol, inputMode, settingsVisible),
    selectIcon(symbol, inputMode)
  );

  return (
    <LongPressProvider onLongPress={onLongPress} delayMS={delayMS}>
      {(longPressProps: LongPressEventProps) => (
        <PressEventProvider onPress={onPress}>
          {(pressProps: PressEventProps) => (
            <Wrapper {...pressProps} className={classNames}>
              <Inner {...longPressProps} data-test='key'>
                {symbol.value}
              </Inner>
            </Wrapper>
          )}
        </PressEventProvider>
      )}
    </LongPressProvider>
  );
};
